// Vuex module for the agent AI reply assistant.
//
// Two concerns, both keyed per conversation:
//   - prefs:    { autoReplyOn } persisted to localStorage (preferences.js).
//   - sessions: the live draft state machine for the streaming UI.
//
// Session status flow:
//   idle -> thinking (request sent, awaiting first token)
//        -> streaming (tokens arriving) -> done
//   any -> failed | unconfigured (error) ; abort -> idle
//
// namespaced:false to match the translation module so getters/actions are reached
// the same way (store.getters.xxx); names are prefixed `aiAssist*` to avoid
// colliding with other root-namespace modules.
import { loadPrefs, savePrefs, DEFAULT_PREFS } from './preferences';
import { requestDraft, abortDraft } from './api';
import logger from './logger';

const resolveAccountId = rootState => {
  const fromRoute = rootState?.route?.params?.accountId;
  if (fromRoute) return fromRoute;
  const fromAccounts = rootState?.accounts?.currentAccountId;
  if (fromAccounts) return fromAccounts;
  const fromActiveAccount = rootState?.userActiveAccount?.id;
  if (fromActiveAccount) return fromActiveAccount;
  if (typeof window !== 'undefined') {
    const match = window.location.pathname.match(/\/accounts\/(\d+)/);
    if (match) return match[1];
  }
  return null;
};

const EMPTY_SESSION = Object.freeze({
  requestId: null,
  status: 'idle',
  text: '',
  baseText: '',
});

const state = () => ({
  prefs: {}, // conversationId -> { autoReplyOn }
  sessions: {}, // conversationId -> { requestId, status, text, baseText }
});

const getters = {
  aiAssistPrefsFor: state => conversationId =>
    state.prefs[conversationId] || { ...DEFAULT_PREFS },
  aiAssistSessionFor: state => conversationId =>
    state.sessions[conversationId] || { ...EMPTY_SESSION },
  // "Busy" = the UI must lock (no sending / other actions), only abort allowed.
  aiAssistBusyFor: state => conversationId => {
    const s = state.sessions[conversationId];
    return !!s && (s.status === 'thinking' || s.status === 'streaming');
  },
};

const mutations = {
  SET_AI_ASSIST_PREFS(state, { conversationId, prefs }) {
    state.prefs = { ...state.prefs, [conversationId]: prefs };
  },
  SET_AI_ASSIST_SESSION(state, { conversationId, session }) {
    state.sessions = { ...state.sessions, [conversationId]: session };
  },
};

const actions = {
  loadAiAssistPrefs({ commit, rootState }, conversationId) {
    const accountId = resolveAccountId(rootState);
    const prefs = loadPrefs(accountId, conversationId);
    commit('SET_AI_ASSIST_PREFS', { conversationId, prefs });
    return prefs;
  },

  updateAiAssistPrefs({ commit, rootState }, { conversationId, patch }) {
    const accountId = resolveAccountId(rootState);
    const current = loadPrefs(accountId, conversationId);
    const next = { ...current, ...patch };
    savePrefs(accountId, conversationId, next);
    commit('SET_AI_ASSIST_PREFS', { conversationId, prefs: next });
    logger.info('prefs.updated', { conversationId, autoReplyOn: next.autoReplyOn });
    return next;
  },

  // Start a draft. `baseText` is prepended to the streamed text; callers pass ''
  // to overwrite the box (manual trigger confirms first; auto only fills an empty
  // box), keeping the hook for future flows that may want to prefix fixed text.
  async aiAssistStart({ commit, getters, rootState }, { conversationId, baseText = '' }) {
    if (getters.aiAssistBusyFor(conversationId)) return;
    const accountId = resolveAccountId(rootState);
    if (!accountId) return;

    commit('SET_AI_ASSIST_SESSION', {
      conversationId,
      session: { requestId: null, status: 'thinking', text: '', baseText },
    });
    logger.info('draft.start', { conversationId, status: 'thinking' });

    try {
      const { request_id: requestId } = await requestDraft({ accountId, conversationId });
      const s = getters.aiAssistSessionFor(conversationId);
      if (s.status === 'thinking') {
        // Still the same attempt: attach the id so tokens/abort can correlate.
        commit('SET_AI_ASSIST_SESSION', { conversationId, session: { ...s, requestId } });
      } else if (s.status === 'idle') {
        // Aborted before the id came back (e.g. clicked interrupt right away):
        // the UI already stopped, but the backend job is mid-flight and never got
        // the signal (it had no id yet). Send the abort now so the brain actually
        // stops instead of running the whole draft dry.
        abortDraft({ accountId, conversationId, requestId }).catch(() => {});
      }
    } catch (err) {
      const httpStatus = err?.response?.status;
      const status = httpStatus === 422 ? 'unconfigured' : 'failed';
      commit('SET_AI_ASSIST_SESSION', {
        conversationId,
        session: { requestId: null, status, text: '', baseText },
      });
      logger.error('draft.requestFailed', { conversationId, status: httpStatus });
    }
  },

  // One streamed token (from ActionCable). Ignore tokens for a stale request.
  aiAssistAppendToken({ commit, getters }, { conversationId, requestId, content }) {
    const s = getters.aiAssistSessionFor(conversationId);
    if (s.requestId && requestId && s.requestId !== requestId) return;
    if (s.status !== 'thinking' && s.status !== 'streaming') return;
    commit('SET_AI_ASSIST_SESSION', {
      conversationId,
      session: {
        ...s,
        requestId: s.requestId || requestId,
        status: 'streaming',
        text: (s.text || '') + (content || ''),
      },
    });
  },

  // Stream ended (completed / aborted / failed by the upstream).
  aiAssistFinish({ commit, getters }, { conversationId, requestId, status }) {
    const s = getters.aiAssistSessionFor(conversationId);
    if (s.requestId && requestId && s.requestId !== requestId) return;
    const nextStatus = status === 'failed' ? 'failed' : 'done';
    commit('SET_AI_ASSIST_SESSION', { conversationId, session: { ...s, status: nextStatus } });
    logger.info('draft.finish', { conversationId, status: nextStatus });
  },

  // Agent pressed "interrupt": stop locally at once, tell the host best-effort.
  async aiAssistAbort({ commit, getters, rootState }, { conversationId }) {
    const accountId = resolveAccountId(rootState);
    const s = getters.aiAssistSessionFor(conversationId);
    // Reset to idle immediately so the UI unlocks and the partial text is kept.
    commit('SET_AI_ASSIST_SESSION', {
      conversationId,
      session: { ...EMPTY_SESSION },
    });
    logger.info('draft.abort', { conversationId });
    if (s.requestId && accountId) {
      try {
        await abortDraft({ accountId, conversationId, requestId: s.requestId });
      } catch (err) {
        logger.warn('draft.abortFailed', { conversationId });
      }
    }
  },

  // Dismiss a finished/failed banner and return the slot to idle.
  aiAssistDismiss({ commit }, { conversationId }) {
    commit('SET_AI_ASSIST_SESSION', { conversationId, session: { ...EMPTY_SESSION } });
  },
};

export default {
  namespaced: false,
  state,
  getters,
  mutations,
  actions,
};
